import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const QuoteForm = ({ requestId, userId }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [existingQuote, setExistingQuote] = useState(null);

  const MAX_DESC = 2000;
  const isValid = price !== '' && Number(price) > 0 && description?.trim()?.length > 0;

  // Step 1: On mount, check if a quote already exists for this professional + request
  useEffect(() => {
    if (!requestId || !userId) return;
    const checkExistingQuote = async () => {
      const { data } = await supabase
        ?.from('quotes')
        ?.select('id, preview_price, preview_text, pdf_url')
        ?.eq('service_request_id', requestId)
        ?.eq('professional_id', userId)
        ?.maybeSingle();
      if (data) {
        setExistingQuote(data);
        // Step 2: Pre-fill fields
        setPrice(String(data?.preview_price ?? ''));
        setDescription(data?.preview_text ?? '');
      }
    };
    checkExistingQuote();
  }, [requestId, userId]);

  const handleFileChange = (file) => {
    if (!file) return;
    if (file?.type !== 'application/pdf') {
      setSubmitError('Solo file PDF sono accettati.');
      return;
    }
    setSubmitError(null);
    setPdfFile(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFileChange(file);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      let pdfUrl = null;

      if (pdfFile) {
        const filePath = `${userId}/${requestId}/${Date.now()}_${pdfFile?.name}`;
        const { error: uploadError } = await supabase?.storage?.from('quote-pdfs')?.upload(filePath, pdfFile, { contentType: 'application/pdf' });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase?.storage?.from('quote-pdfs')?.getPublicUrl(filePath);
        pdfUrl = urlData?.publicUrl || null;
      }

      // Step 3: UPDATE if existingQuote exists, otherwise INSERT
      if (existingQuote) {
        const { error: updateError } = await supabase?.from('quotes')?.update({
          preview_price: Number(price),
          preview_text: description?.trim(),
          pdf_url: pdfFile ? pdfUrl : existingQuote?.pdf_url,
        })?.eq('id', existingQuote?.id);

        if (updateError) throw updateError;

        navigate('/professional-dashboard', { state: { quoteSuccess: true, updated: true } });
      } else {
        // Step 4: Keep current INSERT behavior unchanged
        const { data: newQuote, error: insertError } = await supabase?.from('quotes')?.insert({
          service_request_id: requestId,
          professional_id: userId,
          preview_price: Number(price),
          preview_text: description?.trim(),
          pdf_url: pdfUrl,
        })?.select('id')?.single();

        if (insertError) throw insertError;

        if (newQuote?.id) {
          supabase?.functions?.invoke('notify-client-exclusive-quote', { body: { quote_id: newQuote?.id } })?.catch((e) => console.error('notify-client-exclusive-quote failed:', e));
        }

        navigate('/professional-dashboard', { state: { quoteSuccess: true } });
      }
    } catch (err) {
      console.error('Quote submission error:', err);
      setSubmitError('Errore durante l\'invio del preventivo. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Step 2: Yellow info banner if existingQuote */}
      {existingQuote && (
        <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-300 rounded-lg px-4 py-3 text-sm text-yellow-800">
          <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0 text-yellow-600" />
          <span>Hai già inviato un preventivo. Modifica i campi e salva per aggiornarlo.</span>
        </div>
      )}
      {/* Prezzo stimato */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-900 mb-1">
          Prezzo stimato <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-3">Inserisci il tuo preventivo in Euro</p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-base select-none">€</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e?.target?.value)}
            placeholder="0.00"
            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
            required
          />
        </div>
        {price !== '' && Number(price) <= 0 && (
          <p className="text-xs text-red-500 mt-1">Il prezzo deve essere maggiore di zero.</p>
        )}
      </div>
      {/* Descrizione approccio */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-900 mb-1">
          Descrizione approccio <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-3">Descrivi la tua metodologia, i tempi e il valore che offri</p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e?.target?.value?.slice(0, MAX_DESC))}
          rows={6}
          placeholder="Es. Inizierò con un sopralluogo entro 48 ore per valutare il lavoro. La mia esperienza decennale nel settore mi permette di garantire..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
          required
        />
        <div className="flex justify-end mt-1">
          <span className={`text-xs ${description?.length >= MAX_DESC ? 'text-red-500' : 'text-gray-400'}`}>
            {description?.length}/{MAX_DESC}
          </span>
        </div>
      </div>
      {/* Allega PDF */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-900 mb-1">
          Allega PDF <span className="text-gray-400 font-normal">(opzionale)</span>
        </label>
        <p className="text-xs text-gray-500 mb-3">Portfolio, certificazioni o documentazione di supporto</p>
        <div
          onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef?.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary hover:bg-gray-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleFileChange(e?.target?.files?.[0])}
          />
          {pdfFile ? (
            <div className="flex items-center justify-center gap-3">
              <Icon name="FileText" size={20} className="text-primary" />
              <span className="text-sm text-gray-700 font-medium">{pdfFile?.name}</span>
              <button
                type="button"
                onClick={(e) => { e?.stopPropagation(); setPdfFile(null); }}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Icon name="Upload" size={24} className="text-gray-400" />
              <p className="text-sm text-gray-600">
                <span className="text-primary font-medium">Clicca per caricare</span> o trascina qui
              </p>
              <p className="text-xs text-gray-400">Solo PDF</p>
            </div>
          )}
        </div>
      </div>
      {/* Error */}
      {submitError && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <Icon name="AlertCircle" size={16} />
          <span>{submitError}</span>
        </div>
      )}
      {/* Submit */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        disabled={!isValid || submitting}
        iconName={submitting ? undefined : 'Send'}
        className="w-full"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            {existingQuote ? 'Aggiornamento in corso...' : 'Invio in corso...'}
          </span>
        ) : (
          existingQuote ? 'Aggiorna Preventivo' : 'Invia Preventivo'
        )}
      </Button>
    </form>
  );
};

export default QuoteForm;
